/*
 * Copyright (c) 2020. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy
 */

import { Injectable } from '@angular/core';
import { NotificationModel } from '@app/models/notification.model';
import { Apollo, gql } from 'apollo-angular';
import { MyNotificationModel } from 'app/models/my-notification.model';
import { NotificationUserModel } from 'app/models/notification-user.model';
import { Observable } from 'rxjs';
import { GET_MY_NOTIFICATIONS } from './notification-queries';
import { CaseUsersInputModel } from '@base/projects/image-dx/src/app/models/input/case-users-input.model';
import { CaseModel } from '@base/projects/image-dx/src/app/models/platform/case.model';
import * as uuid from '@base/node_modules/uuid';
import { AuthenticationService } from 'app/services/authentication/authentication.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {

  public myNotifications: MyNotificationModel[];
  public unreadNotificationCount: number;

  constructor(
    private apollo: Apollo,
    private auth: AuthenticationService,
  ) { }

  getMyNotificationsByUserId(userId: string): Observable<MyNotificationModel[]> {
    return new Observable(subscriber => {
      this.apollo.watchQuery({
        query: GET_MY_NOTIFICATIONS,
        variables: {
          condition: {
            userId: userId
          }
        },
        pollInterval: 60000
      }).valueChanges.subscribe((result: any) => {
        this.myNotifications = result.data.notificationUsers.map((notification) => {
          let myNotification = Object.assign(new MyNotificationModel, notification);
          myNotification.notificationDetails = Object.assign(new NotificationModel, myNotification.notification);
          delete myNotification['notification'];
          return myNotification;
        });
        this.myNotifications.sort((a, b) =>
          new Date(b.notificationDetails.created).getTime() - new Date(a.notificationDetails.created).getTime()
        );
        this.updateUnreadNotificationCount();
        subscriber.next(this.myNotifications);
        subscriber.complete();
      });
    })
  }

  public createNotifications(notificationUserPairs: {notification: NotificationModel, notificationUser: NotificationUserModel}[]): Observable<String> {
    let createNotificationInput: string = `mutation createNotifications {`
    notificationUserPairs.forEach((notificationUserPair, index) => {
      createNotificationInput += `
        m${index}: createNotification(
          input: {
            notification: {
              created: "${notificationUserPair.notification.created.toISOString()}",
              notificationId: "${notificationUserPair.notification.notificationId}",
              notificationRead: ${notificationUserPair.notification.notificationRead},
              notificationSourceId: "${notificationUserPair.notification.notificationSourceId}",
              notificationSourceName: "${notificationUserPair.notification.notificationSourceName}",
              notificationType: "${notificationUserPair.notification.notificationType}",
              ownerId: "${notificationUserPair.notification.ownerId}",
              updated: "${notificationUserPair.notification.updated.toISOString()}"
            }
          }
        ) {
          notificationId
        }
        createNotificationUser(
          input: {
            notificationUser: {
              notificationId: "${notificationUserPair.notificationUser.notificationId}",
              userId: "${notificationUserPair.notificationUser.userId}"
            }
          }
        ) {
          notificationId
          userId
        }
      `
    });
    createNotificationInput += `}`;
    return new Observable(subscriber => {
      this.apollo.mutate({
        mutation: gql`${createNotificationInput}`
      }).subscribe(() => {
        subscriber.next();
        subscriber.complete();
      });
    });
  }

  public updateNotificationsById(notificationIds: string[], markRead: boolean): Observable<String> {
    let updateNotificationInput: string = `mutation updateNotifications {`
    notificationIds.forEach((notificationId, index) => {
      updateNotificationInput += `
        m${index}: updateNotification(
          input: {
            notificationId: "${notificationId}",
            patch: {
              notificationRead: ${markRead},
              updated: "${new Date().toISOString()}"
            }
          }
        ) {
          notificationId
        }
      `
    });
    updateNotificationInput += `}`;
    return new Observable(subscriber => {
      this.apollo.mutate({
        mutation: gql`${updateNotificationInput}`
      }).subscribe(() => {
        notificationIds.forEach(notificationId => {
          this.myNotifications.find(notificaton => {
            return notificaton.notificationId === notificationId
          }).notificationDetails.notificationRead = markRead;
        });
        this.updateUnreadNotificationCount();
        subscriber.next();
        subscriber.complete();
      })
    });
  }

  public deleteNotificationsById(notificationIds: string[]): Observable<String> {
    let deleteNotificationInput = `mutation deleteNotification {`
    notificationIds.forEach((notificationId, index) => {
      deleteNotificationInput += `
        m${index}: deleteNotification(
          input: {
            notificationId: "${notificationId}"
          }
        ) {
          notificationId
        }
        m${index + notificationIds.length}: deleteNotificationUser(
          input: {
            notificationId: "${notificationId}"
          }
        ) {
          notificationId
        }
      `
    });
    deleteNotificationInput += `}`;
    return new Observable(subscriber => {
      this.apollo.mutate({
        mutation: gql`${deleteNotificationInput}`
      }).subscribe(() => {
        this.myNotifications = this.myNotifications.filter((notification) => {
          return !notificationIds.includes(notification.notificationDetails.notificationId)
        });
        this.updateUnreadNotificationCount();
        subscriber.next();
        subscriber.complete();
      })
    });
  }

  updateUnreadNotificationCount() {
    this.unreadNotificationCount = this.myNotifications.filter(notification => {
      return !notification.notificationDetails.notificationRead
    }).length;
  }

  createCaseAssignmentNotification(caseUsers: CaseUsersInputModel[], caseUpdate: CaseModel) {
    let notificationUserPairs: {notification: NotificationModel, notificationUser: NotificationUserModel}[] = [];
    caseUsers.forEach(caseUser => {
      const notificationId = uuid.v4();
      const notification = new NotificationModel(
        notificationId,
        'case_assign',
        caseUpdate.caseId,
        this.auth.user.userId,
        new Date(),
        false,
        new Date(),
        caseUpdate.caseName
      );
      const notificationUser = new NotificationUserModel(
        notificationId,
        caseUser.userId
      );
      notificationUserPairs.push({notification: notification, notificationUser: notificationUser});
    })
    this.createNotifications(notificationUserPairs).subscribe(() => {});
  }

  createCaseUnassignmentNotification(caseUsers: CaseUsersInputModel[], caseUpdate: CaseModel) {
    let notificationUserPairs: {notification: NotificationModel, notificationUser: NotificationUserModel}[] = [];
    caseUsers.forEach(caseUser => {
      const notificationId = uuid.v4();
      const notification = new NotificationModel(
        notificationId,
        'case_unassign',
        caseUpdate.caseId,
        this.auth.user.userId,
        new Date(),
        false,
        new Date(),
        caseUpdate.caseName
      );
      const notificationUser = new NotificationUserModel(
        notificationId,
        caseUser.userId
      );
      notificationUserPairs.push({notification: notification, notificationUser: notificationUser});
    })
    this.createNotifications(notificationUserPairs).subscribe(() => {});
  }

  createCaseUpdateNotification(caseUpdate: CaseModel, caseUsers: CaseUsersInputModel[]) {
    let notificationUserPairs: {notification: NotificationModel, notificationUser: NotificationUserModel}[] = [];
    caseUsers.forEach(caseUser => {
      const notificationId = uuid.v4();
      const notification = new NotificationModel(
        notificationId,
        'case_update',
        caseUpdate.caseId,
        this.auth.user.userId,
        new Date(),
        false,
        new Date(),
        caseUpdate.caseName
      );
      const notificationUser = new NotificationUserModel(
        notificationId,
        caseUser.userId
      );
      notificationUserPairs.push({notification: notification, notificationUser: notificationUser});
    })
    this.createNotifications(notificationUserPairs).subscribe(() => {});
  }

  createSlideAssignmentNotifications(caseUpdate: CaseModel) {
    let notificationUserPairs: {notification: NotificationModel, notificationUser: NotificationUserModel}[] = [];
    caseUpdate.caseUsers.forEach(caseUser => {
      const notificationId = uuid.v4();
      const notification = new NotificationModel(
        notificationId,
        'case_slides',
        caseUpdate.caseId,
        this.auth.user.userId,
        new Date(),
        false,
        new Date(),
        caseUpdate.caseName
      );
      const notificationUser = new NotificationUserModel(
        notificationId,
        caseUser.userId
      );
      notificationUserPairs.push({notification: notification, notificationUser: notificationUser});
    })
    this.createNotifications(notificationUserPairs).subscribe(() => {});
  }

}
